import type { PopoverProps, DialogProps, FormProps } from '../../../../core'
import {
  CornerPositionFormField,
  LeftCornerPositionSchema,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Fieldset,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormProvider,
  FormFieldLabel,
  SelectTriggerButton,
  Button,
  DetailsSection,
  DetailsSectionTitle,
  Ellipsis,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Switch,
  ThemeSchema,
  useTheme,
} from '../../../../core'
import {
  AccountSelectionCommand,
  AccountSelectionCommandGroupedOptions,
  AccountSelectionCommandInput,
  AccountSelectionCommandList,
} from '../../../components/navbar/AccountSelectionCommand'
import { RestrictToCapability } from '../../../components/RestrictToCapability'
import { useActiveAccount } from '../../../utils/activeAccountContext'
import { useCurrentUser } from '../../../utils/currentUserContext'
import {
  DemoStreamSpeedSchema,
  DisplaySettingsSchema,
  useDisplaySettings,
} from '../hooks/useDisplaySettings'
import type { components } from '@joyful/openapi/types.gen'
import { zodResolver } from '@hookform/resolvers/zod'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import type { SetRequired } from 'type-fest'
import { z } from 'zod'

export interface DisplaySettingsDialogProps extends SetRequired<
  DialogProps,
  'open' | 'onOpenChange'
> {
  children?: ReactNode
  setDemoMode?: (demoModeAccountSlug: string | null) => void
}

export function DisplaySettingsDialog({
  children,
  setDemoMode,
  ...dialogProps
}: DisplaySettingsDialogProps) {
  return (
    <Dialog {...dialogProps}>
      {children}

      <DialogContent asChild>
        <DisplaySettingsDialogContentForm
          onOpenChange={dialogProps.onOpenChange}
          setDemoMode={setDemoMode}
        />
      </DialogContent>
    </Dialog>
  )
}

const DisplaySettingsFormSchema = z.object({
  demoMode: z.object({
    account_slug: z.string().nullable(),
    isEnabled: z.boolean(),
    stream_speed: DemoStreamSpeedSchema.schema,
  }),
  displaySettings: DisplaySettingsSchema.omit({ demo_mode: true }),
  theme: ThemeSchema.schema.describe('Theme'),
})

interface DisplaySettingsDialogContentFormProps
  extends Partial<FormProps>, Required<Pick<DialogProps, 'onOpenChange'>> {
  setDemoMode?: (demoModeAccountSlug: string | null) => void
}

function DisplaySettingsDialogContentForm({
  onOpenChange,
  setDemoMode,
  ...props
}: DisplaySettingsDialogContentFormProps) {
  const theme = useTheme()
  const [displaySettings, setDisplaySettings] = useDisplaySettings()
  const activeAccount = useActiveAccount()
  const currentUser = useCurrentUser()

  const fallbackDemoAccountSlug =
    activeAccount?.slug ?? currentUser.accounts.at(0)?.slug ?? null
  const defaultDemoAccountSlug =
    displaySettings.demo_mode?.account_slug ?? fallbackDemoAccountSlug

  const form = useForm({
    defaultValues: {
      demoMode: {
        account_slug: defaultDemoAccountSlug,
        isEnabled: !!displaySettings.demo_mode?.account_slug,
        stream_speed: displaySettings.demo_mode?.stream_speed ?? 'default',
      },
      displaySettings: {
        floating_list_navigation_panel_corner:
          displaySettings.floating_list_navigation_panel_corner,
      },
      theme: theme.theme,
    },
    resolver: zodResolver(DisplaySettingsFormSchema),
  })

  const isDemoModeAvailable = currentUser.accounts.length > 0
  const shouldShowDemoModeSettings =
    currentUser.role === 'JOYFUL_ADMIN' && setDemoMode !== undefined

  const isDemoModeEnabled = useWatch({
    control: form.control,
    name: 'demoMode.isEnabled',
  })

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={form.handleSubmit((values) => {
          theme.setTheme(values.theme)
          setDisplaySettings((prev) => {
            return {
              ...prev,
              demo_mode: {
                ...prev.demo_mode,
                stream_speed: values.demoMode.stream_speed,
              },
              floating_list_navigation_panel_corner:
                values.displaySettings.floating_list_navigation_panel_corner,
            }
          })

          const nextDemoModeSlug =
            isDemoModeAvailable &&
            shouldShowDemoModeSettings &&
            values.demoMode.isEnabled &&
            values.demoMode.account_slug !== null
              ? values.demoMode.account_slug
              : null

          if (
            setDemoMode !== undefined &&
            nextDemoModeSlug !== displaySettings.demo_mode?.account_slug
          ) {
            setDemoMode(nextDemoModeSlug)
          }

          onOpenChange(false)
        })}
        shouldSubmitWithHotkey
        {...props}
      >
        <DialogHeader hasDivider>
          <DialogTitle>{`Display settings`}</DialogTitle>
          <DialogDescription className="text-tremor-content">
            {`Customize how the app looks.`}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <Fieldset disabled={form.formState.isSubmitting}>
            <DetailsSection>
              <DetailsSectionTitle size="sm">{`Appearance`}</DetailsSectionTitle>

              <FormField
                control={form.control}
                name="theme"
                render={({ field: { onChange, ref: _, ...field } }) => {
                  return (
                    <FormItem>
                      <FormFieldLabel>{`Theme`}</FormFieldLabel>

                      <div className="grid auto-cols-fr grid-flow-col gap-x-2 self-stretch">
                        {ThemeSchema.definitions.map((definition) => {
                          const Icon = definition.icon
                          const isSelected = field.value === definition.value
                          return (
                            <Button
                              key={definition.value}
                              variant="outline"
                              type="button"
                              isSelected={isSelected}
                              colorVariant={isSelected ? 'default' : 'muted'}
                              onClick={() => {
                                onChange(definition.value)
                              }}
                            >
                              <span className="flex flex-col items-center gap-y-2">
                                <Icon className="size-6" />
                                <span className="text-xs">
                                  {definition.title}
                                </span>
                              </span>
                            </Button>
                          )
                        })}
                      </div>
                    </FormItem>
                  )
                }}
              />

              <RestrictToCapability capability="encounter.details.view">
                <CornerPositionFormField
                  control={form.control}
                  name="displaySettings.floating_list_navigation_panel_corner"
                  definitions={LeftCornerPositionSchema.definitions}
                  label={
                    DisplaySettingsSchema['shape']
                      .floating_list_navigation_panel_corner.description
                  }
                  description={`Controls where the floating list-navigation panel anchors on encounter and request pages. You can also drag the panel up or down to move it between left corners.`}
                />
              </RestrictToCapability>
            </DetailsSection>

            {shouldShowDemoModeSettings ? (
              <>
                <Separator thickness="thick" />

                <DetailsSection>
                  <DetailsSectionTitle size="sm">
                    {`Demo mode`}
                  </DetailsSectionTitle>

                  <FormField
                    control={form.control}
                    name="demoMode.isEnabled"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <div className="flex items-center gap-x-2 self-stretch">
                            <FormLabel>{`Enable demo mode`}</FormLabel>
                            <div className="flex min-h-6.5 flex-1 items-center justify-between gap-x-2">
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  disabled={!isDemoModeAvailable}
                                  onCheckedChange={(isChecked) => {
                                    field.onChange(isChecked)

                                    if (
                                      isChecked &&
                                      form.getValues(
                                        'demoMode.account_slug',
                                      ) === null &&
                                      fallbackDemoAccountSlug !== null
                                    ) {
                                      form.setValue(
                                        'demoMode.account_slug',
                                        fallbackDemoAccountSlug,
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        },
                                      )
                                    }
                                  }}
                                />
                              </FormControl>
                              {isDemoModeEnabled ? (
                                <FormField
                                  control={form.control}
                                  name="demoMode.account_slug"
                                  render={({ field: slugField }) => {
                                    return (
                                      <DemoAccountSelectPopover
                                        modal
                                        accounts={currentUser.accounts}
                                        selectedAccountSlug={slugField.value}
                                        onSelectAccountSlug={slugField.onChange}
                                      />
                                    )
                                  }}
                                />
                              ) : null}
                            </div>
                          </div>
                          <FormDescription>
                            {isDemoModeAvailable
                              ? `Blurs PHI and PII on this device for screen-sharing and screenshots.`
                              : `You need at least one account to enable demo mode.`}
                          </FormDescription>
                        </FormItem>
                      )
                    }}
                  />

                  {isDemoModeEnabled ? (
                    <FormField
                      control={form.control}
                      name="demoMode.stream_speed"
                      render={({ field: { onChange, ref: _, ...field } }) => {
                        return (
                          <FormItem>
                            <FormFieldLabel>{`Demo agent stream speed`}</FormFieldLabel>
                            <div className="grid auto-cols-fr grid-flow-col gap-x-2 self-stretch">
                              {DemoStreamSpeedSchema.options().map(
                                ({ label, value }) => {
                                  const isSelected = field.value === value
                                  return (
                                    <Button
                                      key={value}
                                      type="button"
                                      variant="outline"
                                      isSelected={isSelected}
                                      colorVariant={
                                        isSelected ? 'default' : 'muted'
                                      }
                                      onClick={() => {
                                        onChange(value)
                                      }}
                                    >
                                      {label}
                                    </Button>
                                  )
                                },
                              )}
                            </div>
                            <FormDescription>
                              {`Controls the cadence of the scripted encounter agent demo replay.`}
                            </FormDescription>
                          </FormItem>
                        )
                      }}
                    />
                  ) : null}
                </DetailsSection>
              </>
            ) : null}
          </Fieldset>
        </DialogBody>

        <DialogFooter hasDivider>
          <DialogClose asChild>
            <Button variant="outline">{`Cancel`}</Button>
          </DialogClose>

          <Button
            disabled={form.formState.isDirty === false}
            variant="brand-subtle"
            type="submit"
          >
            {`Save`}
          </Button>
        </DialogFooter>
      </Form>
    </FormProvider>
  )
}

type CurrentUserAccount = components['schemas']['CurrentUserAccountRead']

type DemoAccountSelectPopoverProps = {
  accounts: Array<CurrentUserAccount>
  onSelectAccountSlug: (slug: string) => void
  selectedAccountSlug: string | null
} & Omit<PopoverProps, 'open' | 'onOpenChange'>

function DemoAccountSelectPopover({
  accounts,
  onSelectAccountSlug,
  selectedAccountSlug,
  ...popoverProps
}: DemoAccountSelectPopoverProps) {
  const [isPopoverOpen, setPopoverOpen] = useState(false)
  const selectedAccount =
    accounts.find((account) => {
      return account.slug === selectedAccountSlug
    }) ?? null

  return (
    <Popover
      {...popoverProps}
      open={isPopoverOpen}
      onOpenChange={setPopoverOpen}
    >
      <PopoverTrigger asChild>
        <FormControl>
          <div className="min-w-40">
            <SelectTriggerButton size="xs" width="full">
              {selectedAccount === null ? (
                <span className="text-tremor-content">{`Select account…`}</span>
              ) : (
                <Ellipsis>{selectedAccount.name}</Ellipsis>
              )}
            </SelectTriggerButton>
          </div>
        </FormControl>
      </PopoverTrigger>

      <PopoverContent size="sm" padding="none" align="end">
        <AccountSelectionCommand
          actions={{
            onSelect: (account) => {
              if (account?.slug) {
                onSelectAccountSlug(account.slug)
              }

              setPopoverOpen(false)
            },
          }}
          state={{
            accounts,
            activeAccount: selectedAccount,
            isDisabled: false,
            size: 'sm',
          }}
        >
          <AccountSelectionCommandInput placeholder="Search accounts…" />
          <AccountSelectionCommandList>
            <AccountSelectionCommandGroupedOptions />
          </AccountSelectionCommandList>
        </AccountSelectionCommand>
      </PopoverContent>
    </Popover>
  )
}
